import z from 'zod';
import chalk from 'chalk';
import {
  renderDiff,
  renderDivider,
  renderTestCaseHeader,
  renderShortAnswer,
} from './diff-display';
import models from '../../models';
import output from '../output-writer';
import file from '../file';

export interface FormatterOptions {
  outputFile?: string;
}

export type TestCaseResult = z.infer<typeof models.api.testActionResultSchema>;

function isErrorResult(
  result: TestCaseResult
): result is { error: string; alertLink?: string; expected?: string } {
  return 'error' in result;
}

function renderError(errorMessage: string) {
  return chalk.bgRed.white(` ERROR: ${errorMessage} `);
}

function formatResultForDisplay(
  result: TestCaseResult,
  index: number,
  total: number,
  actionTitle: string
) {
  const { alertLink } = result;
  output.log(renderTestCaseHeader(index, total, alertLink, actionTitle));

  if (isErrorResult(result)) {
    if (result.expected) {
      output.log(`${chalk.bold('Expected:')} ${result.expected}`);
      output.log('');
    }
    output.log(renderError(result.error));
    output.log('');
    return;
  }

  const insights = result.actual.insights ?? '(no insights returned)';
  output.log(renderDiff(result.expected, insights));

  output.log(renderShortAnswer(result.actual.shortAnswer));
  output.log('');
}

function formatResultForFile(
  result: TestCaseResult,
  index: number,
  total: number,
  actionTitle: string
) {
  const alertLine = result.alertLink ? `Alert: ${result.alertLink}\n` : '';
  const alertDetailsLine = result.alertDetails
    ? `Alert Details:\n${JSON.stringify(result.alertDetails, null, 2)}\n`
    : '';
  const actionLine = `Action: ${actionTitle}\n`;

  if (isErrorResult(result)) {
    const expectedLine = result.expected ? `Expected:\n${result.expected}` : '';
    return [
      `Test Case ${String(index + 1)}/${String(total)}`,
      alertLine + alertDetailsLine + actionLine,
      expectedLine,
      `ERROR: ${result.error}`,
      renderDivider(),
    ]
      .filter((line) => line !== '')
      .join('\n\n');
  }

  const rawResponse = JSON.stringify(result.actual.rawResponse, null, 2);

  return [
    `Test Case ${String(index + 1)}/${String(total)}`,
    alertLine + alertDetailsLine + actionLine,
    `Expected:\n${result.expected}`,
    `Actual (insights):\n${result.actual.insights ?? '(no insights returned)'}`,
    `Short Answer:\n${result.actual.shortAnswer ?? '(none)'}`,
    `Full Summary:\n${result.actual.response.summary}`,
    `Raw Response:\n${rawResponse}`,
    renderDivider(),
  ].join('\n\n');
}

export async function displayTestResults(
  results: TestCaseResult[],
  actionTitle: string,
  options: FormatterOptions = {}
) {
  const total = results.length;

  results.forEach((result, i) => {
    formatResultForDisplay(result, i, total, actionTitle);
  });

  if (options.outputFile) {
    const fileContent = results
      .map((result, i) => formatResultForFile(result, i, total, actionTitle))
      .join('\n');
    await file.writeFile(options.outputFile, fileContent);
    output.info(`Details written to ${options.outputFile}`);
  }
}

export default { displayTestResults };
